import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core'
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatButton } from '@angular/material/button'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { SnackbarCustomService } from '../../../../shared/components/snackbar/snackbar-custom.service'
import {
  ErrorRequestModel,
  ResponseRequestModel,
} from '../../../../core/models/response.model'
import { MatInputModule } from '@angular/material/input'
import { MatOption } from '@angular/material/core'
import { MatSelect } from '@angular/material/select'
import { BankService } from '../../../../core/services/bank/bank/bank.service'
import { BankDetailsService } from '../../../../core/services/bank/bank-details/bank-details.service'
import { AsyncPipe, JsonPipe } from '@angular/common'
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete'
import { BehaviorSubject, map, Observable, startWith } from 'rxjs'
import { removeAccents } from '../../../../shared/utils/utils'

@Component({
  selector: 'bank-details-client-edit-form',
  imports: [
    FormsModule,
    MatButton,
    MatProgressSpinner,
    ReactiveFormsModule,
    MatInputModule,
    MatOption,
    MatSelect,
    AsyncPipe,
    MatAutocomplete,
    MatAutocompleteTrigger,
    JsonPipe,
  ],
  templateUrl: './bank-details-client-edit-form.component.html',
  styleUrl: './bank-details-client-edit-form.component.scss',
  providers: [BankService, BankDetailsService],
  standalone: true,
})
export class BankDetailsClientEditFormComponent implements OnInit, OnChanges {
  constructor(
    private formBuilder: FormBuilder,
    private snackbar: SnackbarCustomService,
    private bankService: BankService,
    private bankDetailsService: BankDetailsService,
  ) {}

  form: FormGroup = new FormGroup({})
  loading = false

  @Output() response = new EventEmitter()
  @Input() editItem: any = {}
  @Input() personId = 0

  private listTypeSubject = new BehaviorSubject<any[]>([])
  listType$ = this.listTypeSubject.asObservable()
  listTypeFiltered: Observable<any> | undefined

  ngOnInit(): void {
    this.getListType()
    this.createForm()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['editItem']) {
      this.treatmentItem()
    }
  }

  private treatmentItem() {
    this.resetForm()
    if (this.checkEdit()) {
      this.fillForm()
    }
  }

  displayFn = (id: number): string => {
    const listType = this.listTypeSubject.getValue()
    if (!listType || !id) {
      return ''
    }
    const selectedBank = listType.find((ele) => ele.id === id)
    return selectedBank ? selectedBank.descricao : ''
  }

  private _filter(value: any): any[] {
    const listType = this.listTypeSubject.getValue()
    if (typeof value === 'string') {
      const filterValue = value.toLowerCase()
      return listType.filter(
        (ele) =>
          removeAccents(ele.descricao.toLowerCase()).includes(filterValue) ||
          ele.cod_banco.toLowerCase().includes(filterValue),
      )
    } else {
      return listType
    }
  }

  getListType() {
    this.bankService.listBank().subscribe({
      next: (response: ResponseRequestModel) => {
        const listType = response.data.map((item: any) => ({
          id: item.id,
          cod_banco: item.codigoBanco,
          descricao: item.nome,
        }))
        this.listTypeSubject.next(listType)

        this.listTypeFiltered = this.form.get('bank')!.valueChanges.pipe(
          startWith(''),
          map((value) => this._filter(value)),
        )
      },
      error: (err: ResponseRequestModel) => {
        console.error(err)
      },
      complete: () => {},
    })
  }

  checkBank() {
    const bank = this.form.get('bank')?.value
    if (bank && typeof bank == 'number') {
      return true
    } else {
      return false
    }
  }

  checkEdit() {
    if (this.editItem) {
      if (this.editItem.edit) {
        return true
      } else {
        return false
      }
    } else {
      return false
    }
  }

  fillForm() {
    this.form.get('bank')?.setValue(this.editItem.data.bancoId)
    this.form.get('bank_branch')?.setValue(this.editItem.data.agencia)
    this.form.get('bank_account')?.setValue(this.editItem.data.conta)
    this.form.get('pix')?.setValue(this.editItem.data.pix)
  }

  resetForm() {
    this.form.reset()
  }

  createForm(): void {
    this.form = this.formBuilder.group({
      bank: ['', [Validators.required]],
      bank_branch: ['', [Validators.required]],
      bank_account: ['', [Validators.required]],
      pix: [''],
    })
  }

  createBankDetails() {
    this.loading = true
    const model = this.createModel()
    this.bankDetailsService.createBankDetails(model).subscribe({
      next: (response: any) => {
        this.snackbar.success(response.message)
        this.response.emit({ refresh: true, clear: true })
        this.resetForm()
        this.loading = false
      },
      error: (err: ErrorRequestModel) => {
        this.loading = false
        this.snackbar.errorRequest(err)
      },
      complete: () => {
        this.loading = false
      },
    })
  }

  editBankDetails() {
    this.loading = true
    const model = this.createModel(true)
    this.bankDetailsService.editBankDetails(model).subscribe({
      next: (response: any) => {
        this.snackbar.success(response.message)
        this.response.emit({ refresh: true, clear: true })
        this.resetForm()
        this.loading = false
      },
      error: (err: ErrorRequestModel) => {
        this.loading = false
        this.snackbar.errorRequest(err)
      },
      complete: () => {
        this.loading = false
      },
    })
  }

  cancel() {
    this.response.emit({ refresh: false, clear: true })
    this.resetForm()
  }

  createModel(edit = false) {
    if (edit) {
      return {
        id: this.editItem.data.id,
        pessoaId: this.personId,
        bancoId: this.form.controls['bank'].value,
        agencia: this.form.controls['bank_branch'].value,
        conta: this.form.controls['bank_account'].value,
        pix: this.form.controls['pix'].value,
      }
    } else {
      return {
        pessoaId: this.personId,
        bancoId: this.form.controls['bank'].value,
        agencia: this.form.controls['bank_branch'].value,
        conta: this.form.controls['bank_account'].value,
        pix: this.form.controls['pix'].value,
      }
    }
  }
}
